import React, { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const BOSS_WHATSAPP = "+584248476761";

// Self-service cards — shown by country
const COUNTRY_META = {
  VE: { flag: "🇻🇪", name: "Venezuela", selfPay: false },
  CO: { flag: "🇨🇴", name: "Colombia", selfPay: true },
  BR: { flag: "🇧🇷", name: "Brasil",   selfPay: true },
  ZA: { flag: "🇿🇦", name: "South Africa", selfPay: true },
  DO: { flag: "🇩🇴", name: "Dominicana", selfPay: true },
  TT: { flag: "🇹🇹", name: "Trinidad", selfPay: true },
  CR: { flag: "🇨🇷", name: "Costa Rica", selfPay: true },
  PA: { flag: "🇵🇦", name: "Panamá", selfPay: true },
};

const waLink = (text) => {
  const url = `https://wa.me/${BOSS_WHATSAPP.replace(/\D/g, "")}?text=${encodeURIComponent(text)}`;
  return url;
};

/**
 * Floating widgets for Sellox landing pages:
 *  - WhatsApp click-to-chat (right)
 *  - AI customer service chatbot (left)
 *  - 7-day trial sign-up modal (triggered externally via onOpenSignup prop)
 *
 * Usage:
 *   <LandingWidgets country="VE" />
 */
export default function LandingWidgets({ country = "VE" }) {
  const meta = COUNTRY_META[country] || COUNTRY_META.VE;

  // ── Chat state ──
  const [chatOpen, setChatOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    {
      role: "assistant",
      text: {
        VE: "¡Hola! Soy el asistente de Sellox 🤖 ¿En qué puedo ayudarte hoy?",
        CO: "¡Hola! Soy el asistente de Sellox 🤖 ¿En qué puedo ayudarte hoy?",
        BR: "Olá! Sou o assistente da Sellox 🤖 Como posso ajudar?",
        ZA: "Hi! I'm the Sellox assistant 🤖 How can I help you today?",
        DO: "¡Hola! Soy el asistente de Sellox 🤖 ¿En qué puedo ayudarte hoy?",
        TT: "Hi! I'm the Sellox assistant 🤖 How can I help you today?",
        CR: "¡Hola! Soy el asistente de Sellox 🤖 ¿En qué puedo ayudarte hoy?",
        PA: "¡Hola! Soy el asistente de Sellox 🤖 ¿En qué puedo ayudarte hoy?",
      }[country] || "Hi! I'm the Sellox assistant 🤖 How can I help you today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, chatOpen]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMsgs((prev) => [...prev, { role: "user", text }]);
    setSending(true);
    try {
      const r = await axios.post(`${API}/ai/chatbot`, {
        message: text,
        session_id: sessionId,
        country_code: country,
      });
      setSessionId(r.data.session_id);
      setMsgs((prev) => [...prev, { role: "assistant", text: r.data.reply }]);
    } catch (e) {
      setMsgs((prev) => [
        ...prev,
        {
          role: "assistant",
          text:
            "😕 Lo siento, el asistente está temporalmente fuera de servicio. Por favor escríbenos por WhatsApp al " +
            BOSS_WHATSAPP,
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* ─── Floating AI chatbot button (left) ─── */}
      {!chatOpen && (
        <button
          onClick={() => setChatOpen(true)}
          className="fixed bottom-5 left-5 z-[9000] bg-gradient-to-br from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all group"
          data-testid="landing-ai-chat-btn"
          aria-label="Open AI assistant"
        >
          <Sparkles className="w-7 h-7" />
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold border-2 border-white">
            AI
          </span>
        </button>
      )}

      {/* ─── AI Chatbot panel ─── */}
      {chatOpen && (
        <div
          className="fixed bottom-5 left-5 z-[9001] w-[min(92vw,380px)] h-[min(80vh,560px)] bg-slate-900/95 backdrop-blur-lg border border-violet-500/40 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4"
          data-testid="landing-ai-chat-panel"
        >
          <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <Sparkles className="w-5 h-5" />
              <div className="leading-tight">
                <div className="font-bold text-sm">Sellox AI {meta.flag}</div>
                <div className="text-[10px] opacity-75">Customer service · 24/7</div>
              </div>
            </div>
            <button
              onClick={() => setChatOpen(false)}
              className="text-white/80 hover:text-white p-1 rounded"
              data-testid="landing-ai-chat-close-btn"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 text-sm">
            {msgs.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`rounded-2xl px-3 py-2 max-w-[85%] whitespace-pre-wrap leading-relaxed ${
                    m.role === "user"
                      ? "bg-violet-600 text-white"
                      : "bg-slate-800 text-slate-100 border border-slate-700"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-slate-400 text-xs italic px-2">
                <Loader2 className="w-3 h-3 animate-spin" /> thinking…
              </div>
            )}
          </div>

          <div className="border-t border-slate-700 p-2 bg-slate-900">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Type your question…"
                className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-500"
                data-testid="landing-ai-chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={sending || !input.trim()}
                className="bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white rounded-lg px-3 py-2"
                data-testid="landing-ai-chat-send-btn"
                aria-label="Send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-slate-500 mt-1 text-center">
              Also reach us on{" "}
              <a
                href={waLink("Hello, I have a question about Sellox.")}
                target="_blank"
                rel="noreferrer"
                className="text-emerald-400 hover:underline"
              >
                WhatsApp
              </a>{" "}
              anytime.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Trial-signup modal — exported separately so pages can trigger via their own
 * "Start Free Trial" buttons. Country-specific copy + behavior.
 */
export function TrialSignupModal({ open, onClose, country = "VE" }) {
  const meta = COUNTRY_META[country] || COUNTRY_META.VE;
  const [shopName, setShopName] = useState("");
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [registerLink, setRegisterLink] = useState("/register");

  if (!open) return null;

  const L = {
    title: {
      es: `Prueba gratis 7 días — ${meta.flag} ${meta.name}`,
      en: `7-Day Free Trial — ${meta.flag} ${meta.name}`,
    },
    shop: { es: "Nombre de tu tienda", en: "Your shop name" },
    contact: { es: "Tu nombre", en: "Your name" },
    phone: { es: "Teléfono / WhatsApp", en: "Phone / WhatsApp" },
    email: { es: "Email (opcional)", en: "Email (optional)" },
    notes: { es: "Notas (opcional)", en: "Notes (optional)" },
    submit: { es: "Solicitar prueba", en: "Request trial" },
    helper: {
      VE: "Tras enviar, creas tu cuenta tú mismo en 1 minuto y la prueba de 7 días queda activa al instante (sin tarjeta). También te contactamos por WhatsApp.",
      CO: "Tras enviar el formulario te contactamos por WhatsApp. También puedes pagar en línea directamente con tarjeta y auto-activar.",
      BR: "Após enviar você será contatado via WhatsApp. Você também pode pagar online com cartão e ativar automaticamente.",
      ZA: "After submitting you'll be contacted on WhatsApp. You can also pay online by card for instant activation.",
      DO: "Tras enviar recibirás respuesta por WhatsApp. También puedes pagar en línea con tarjeta.",
      TT: "After submitting you'll be contacted on WhatsApp. You can also pay online by card.",
      CR: "Tras enviar recibirás respuesta por WhatsApp. También puedes pagar en línea con tarjeta.",
      PA: "Tras enviar recibirás respuesta por WhatsApp. También puedes pagar en línea con tarjeta y auto-activar.",
    }[country],
  };
  const lang = country === "BR" ? "es" : country === "ZA" || country === "TT" ? "en" : "es";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!shopName.trim() || !contactName.trim() || !phone.trim()) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/ai/trial-signup`, {
        shop_name: shopName.trim(),
        contact_name: contactName.trim(),
        phone: phone.trim(),
        email: email.trim(),
        country_code: country,
        notes: notes.trim(),
      });
      setSuccess(true);
      setRegisterLink(r.data?.register_link || "/register");
      // Open a pre-filled WhatsApp message to the boss for instant follow-up
      if (r.data?.boss_whatsapp_link) {
        window.open(r.data.boss_whatsapp_link, "_blank", "noopener");
      }
    } catch (err) {
      alert("❌ Error sending request. Please contact us directly on WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9500] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-slate-900 border border-violet-500/40 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-4 flex items-center justify-between">
          <h3 className="text-white font-bold text-base">{L.title[lang]}</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          <div className="p-6 text-center space-y-4">
            <div className="text-5xl">✅</div>
            <h4 className="text-xl font-bold text-white">¡Enviado! / Sent!</h4>
            <p className="text-slate-300 text-sm leading-relaxed">
              {lang === "en"
                ? "Next step: create your account yourself in 1 minute — your 7-day free trial starts instantly (no card). We'll also reach out on WhatsApp."
                : "Siguiente paso: crea tu cuenta tú mismo en 1 minuto — tu prueba gratis de 7 días queda activa al instante (sin tarjeta). También te escribiremos por WhatsApp."}
            </p>
            <a
              href={registerLink}
              className="block w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 px-5 py-3 rounded-xl font-bold text-base transition-colors"
              data-testid="trial-success-register-btn"
            >
              {lang === "en" ? "🚀 Create my account now (1 min)" : "🚀 Crear mi cuenta ahora (1 min)"}
            </a>
            <a
              href={waLink("Acabo de enviar el formulario de prueba de Sellox. ¿Podemos hablar?")}
              target="_blank"
              rel="noreferrer"
              className="inline-block bg-[#25D366] hover:bg-[#128C7E] text-white px-5 py-2 rounded-lg font-medium"
              data-testid="trial-success-whatsapp-btn"
            >
              💬 Chat on WhatsApp now
            </a>
            <button
              onClick={onClose}
              className="block w-full text-slate-400 hover:text-white text-sm"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-3">
            <p className="text-amber-300 text-xs bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              💡 {L.helper}
            </p>

            <div>
              <label className="text-xs text-slate-400">{L.shop[lang]} *</label>
              <input
                value={shopName}
                onChange={(e) => setShopName(e.target.value)}
                required
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500"
                data-testid="trial-shop-name-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400">{L.contact[lang]} *</label>
              <input
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                required
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500"
                data-testid="trial-contact-name-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400">{L.phone[lang]} *</label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                placeholder="+58 412 1234567"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500"
                data-testid="trial-phone-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400">{L.email[lang]}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500"
                data-testid="trial-email-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400">{L.notes[lang]}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500"
                data-testid="trial-notes-input"
              />
            </div>

            {country === "VE" ? (
              /* VE: only WhatsApp signup (no self-serve payment) */
              <button
                type="submit"
                disabled={submitting || !shopName.trim() || !contactName.trim() || !phone.trim()}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 disabled:opacity-50 text-white font-bold py-3 rounded-lg transition-all"
                data-testid="trial-submit-btn"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin inline" /> : L.submit[lang]}
              </button>
            ) : (
              /* Non-VE: two options — instant Stripe pay OR WhatsApp */
              <div className="space-y-3">
                <button
                  type="button"
                  disabled={submitting || !shopName.trim() || !contactName.trim() || !phone.trim() || !email.trim()}
                  onClick={() => {
                    if (!email.trim()) { alert(lang === "en" ? "Email required for instant activation" : "Email requerido para activación instantánea"); return; }
                    const params = new URLSearchParams({
                      country,
                      shop: shopName.trim(),
                      name: contactName.trim(),
                      phone: phone.trim(),
                      email: email.trim(),
                    });
                    window.location.href = `/saas/checkout?${params.toString()}`;
                  }}
                  className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 disabled:opacity-40 text-white font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2"
                  data-testid="trial-pay-now-btn"
                >
                  💳 {lang === "en" ? "Pay & activate instantly (2 min)" : "Pagar y activar ahora (2 min)"}
                </button>
                <div className="relative flex items-center">
                  <div className="flex-grow border-t border-slate-700"></div>
                  <span className="mx-3 text-xs text-slate-500 uppercase">{lang === "en" ? "or" : "o"}</span>
                  <div className="flex-grow border-t border-slate-700"></div>
                </div>
                <button
                  type="submit"
                  disabled={submitting || !shopName.trim() || !contactName.trim() || !phone.trim()}
                  className="w-full bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-lg transition-all border border-slate-700 flex items-center justify-center gap-2"
                  data-testid="trial-submit-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>💬 {lang === "en" ? "Request via WhatsApp" : "Solicitar por WhatsApp"}</>}
                </button>
              </div>
            )}

            <p className="text-[11px] text-slate-500 text-center pt-1">
              💬 ¿Dudas? Chatea con nuestro{" "}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  // small delay so the chat button is reachable
                  setTimeout(() => {
                    document.querySelector('[data-testid="landing-ai-chat-btn"]')?.click();
                  }, 120);
                }}
                className="text-violet-400 hover:underline font-medium"
              >
                asistente AI
              </button>{" "}
              o WhatsApp{" "}
              <a
                href={waLink("Hola, tengo una duda sobre Sellox.")}
                target="_blank"
                rel="noreferrer"
                className="text-emerald-400 hover:underline font-medium"
              >
                {BOSS_WHATSAPP}
              </a>
              .
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export { MessageCircle };
